import { FC } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TermsDialog: FC<TermsDialogProps> = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Políticas y Condiciones de FinancieraMente.Kids
          </DialogTitle>
          <DialogDescription>
            Por favor, lee atentamente nuestras políticas y condiciones
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 px-2">
          <div className="space-y-6 pb-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Introducción</h2>
              <p className="text-gray-600">
                FinancieraMente.Kids es una plataforma educativa diseñada para niños de 8 a 12 años, 
                dedicada a la enseñanza de educación financiera. Este documento establece las condiciones 
                de uso y las políticas que rigen nuestra plataforma.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Consentimiento Parental</h2>
              <p className="text-gray-600 mb-2">Al registrarse en FinancieraMente.Kids, usted declara que:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Es el padre, madre o tutor legal del menor que utilizará la plataforma</li>
                <li>Tiene la capacidad legal para aceptar estos términos en nombre del menor</li>
                <li>Autoriza expresamente el uso de la plataforma por parte del menor bajo su supervisión</li>
                <li>Comprende y acepta que es responsable de supervisar la actividad del menor en la plataforma</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Protección de Datos Personales</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">3.1 Datos que Recolectamos</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Del tutor: nombre, correo electrónico, número de WhatsApp</li>
                    <li>Del menor: nombre y progreso educativo</li>
                    <li>No recolectamos datos sensibles de los menores</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">3.2 Uso de los Datos</h3>
                  <p className="text-gray-600 mb-2">Los datos se utilizan exclusivamente para:</p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Proporcionar acceso a la plataforma educativa</li>
                    <li>Realizar seguimiento del progreso educativo</li>
                    <li>Comunicar actualizaciones importantes</li>
                    <li>Brindar soporte técnico</li>
                    <li>Mejorar la experiencia educativa</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">3.3 Protección de Datos</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Cumplimos con el RGPD (Europa), COPPA (EE.UU.), LEPINA (El Salvador) y regulaciones similares</li>
                    <li>Los datos se almacenan de forma segura y encriptada</li>
                    <li>No compartimos datos con terceros sin consentimiento expreso</li>
                    <li>No utilizamos los datos para publicidad dirigida</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Seguridad y Privacidad</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Todas las interacciones están monitoreadas para garantizar un entorno seguro</li>
                <li>No permitimos comunicación directa entre usuarios</li>
                <li>No mostramos información personal de los menores a otros usuarios</li>
                <li>Implementamos medidas de control parental</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Contenido y Actividades</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Todo el contenido es apropiado para la edad objetivo</li>
                <li>El material educativo está diseñado por expertos en educación financiera</li>
                <li>No incluimos publicidad de terceros</li>
                <li>No permitimos transacciones monetarias reales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Derechos del Usuario</h2>
              <p className="text-gray-600 mb-2">Los tutores tienen derecho a:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Acceder y modificar los datos personales</li>
                <li>Solicitar la eliminación de la cuenta y datos asociados</li>
                <li>Recibir copia de los datos almacenados</li>
                <li>Revocar el consentimiento en cualquier momento</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Responsabilidades</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">7.1 De la Plataforma</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Mantener la seguridad de los datos</li>
                    <li>Proporcionar el servicio educativo acordado</li>
                    <li>Responder a consultas y problemas técnicos</li>
                    <li>Mantener el contenido actualizado y apropiado</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">7.2 Del Tutor</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Supervisar el uso de la plataforma por el menor</li>
                    <li>Mantener seguras las credenciales de acceso</li>
                    <li>Reportar cualquier uso indebido</li>
                    <li>Asegurar que el menor comprende las normas básicas de uso</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Planes y Suscripciones</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Los precios y condiciones se muestran claramente antes de la suscripción</li>
                <li>Ofrecemos período de prueba gratuito</li>
                <li>Las cancelaciones pueden realizarse en cualquier momento dentro de la plataforma</li>
                <li>No se realizan reembolsos; la suscripción permanece activa hasta el final del período pagado</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Cambios en las Políticas</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Notificaremos cambios importantes por correo electrónico</li>
                <li>Los cambios entrarán en vigor 30 días después de la notificación</li>
                <li>El uso continuado implica la aceptación de los cambios</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Contacto</h2>
              <p className="text-gray-600 mb-2">Para cualquier consulta sobre estas políticas:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Email: email@financieramente.kids</li>
                <li>WhatsApp: +59898801814</li>
                <li>Formulario de contacto en la plataforma</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Marco Legal</h2>
              <p className="text-gray-600 mb-2">Estas políticas cumplen con:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Leyes de protección de datos personales</li>
                <li>Normativas de protección al menor</li>
                <li>Regulaciones de comercio electrónico</li>
                <li>Leyes de protección al consumidor</li>
              </ul>
            </section>

            <section className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-700 font-medium">
                Al marcar la casilla de aceptación, usted confirma que:
              </p>
              <ul className="list-decimal pl-6 text-gray-600 space-y-1 mt-2">
                <li>Ha leído y comprendido estas políticas</li>
                <li>Acepta todos los términos y condiciones</li>
                <li>Autoriza el uso de la plataforma por el menor bajo su tutela</li>
                <li>Comprende sus derechos y responsabilidades</li>
              </ul>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
